import { DataModelFile } from '@blazemeter/blaze-data-model';
import {
  CreateFolderPayload,
  DataDefinitionPanel,
  DEFAULT_LIMIT,
  Paging,
  SharedFolder,
  SharedFoldersActions,
  SharedFoldersData,
} from '@blazemeter/blaze-data-ui';
import _ from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import store from '../../store/configureStore';
import {
  deleteCSVFile,
  downloadCSVFile,
  loadDataModelCsvFiles,
  uploadCSVFiles,
} from '../../store/dataModel/dataModelActions';
import { setDataModelSharedFolders } from '../../store/dataModel/dataModelReducer';
import { DataModelState } from '../../store/dataModel/dataModelTypes';
import {
  createFolder,
  deleteWorkspaceSharedFolder,
  deleteWorkspaceSharedFolderFile,
  fetchFolder,
  loadFolder,
  loadFolders,
  uploadFileToSharedFolder,
} from '../../store/sharedFolders/sharedFoldersActions';
import { SharedFoldersState } from '../../store/sharedFolders/sharedFoldersTypes';
import { Service } from '../../types/rest/assetCatalogTypes';
import MixpanelService from '../../utils/mixpanelService';
import EmptyDataPanel from './EmptyDataPanel';
import GlobalScopeExtension from './GlobalScope/GlobalScopeExtension';
import { showTDMMessage, TDMMessageType } from './TDMCommon';

interface DataDefinitionPanelAdapterProps {
  workspaceId: number;
  onClose(): void;
  selectedServiceId: number | null;
  services: Service[];
  sharedFolders: SharedFoldersState;
  isPrivateDataAccount: boolean;
  accountId: number;
  blazeBaseUrl: string;
  dataModel: DataModelState;
  onChangeService: (serviceId: number) => void;
  showIterations?: boolean;
  filterServiceIds?: number[];
}

type AppDispatch = ThunkDispatch<DataModelState, any, AnyAction>;

const DataDefinitionPanelAdapter = (props: DataDefinitionPanelAdapterProps) => {
  const dispatch = useDispatch();
  const thunkDispatch: AppDispatch = useDispatch();

  const refLoadingFiles = useRef(props.sharedFolders.loadingFiles);
  useEffect(() => {
    refLoadingFiles.current = props.sharedFolders.loadingFiles;
  }, [props.sharedFolders.loadingFiles]);

  useEffect(() => {
    dispatch(loadFolders(props.workspaceId, { skip: 0, limit: DEFAULT_LIMIT }));
  }, []);

  useEffect(() => {
    // load the CSV files on every change of the service
    if (props.selectedServiceId && props.dataModel.dataModel) {
      dispatch(loadDataModelCsvFiles());
    }
  }, [props.selectedServiceId]);

  const handleUploadCsvFile = (files: any[]) => {
    return new Promise<void>(async resolve => {
      await thunkDispatch(uploadCSVFiles(files));
      resolve();
    });
  };

  const onGetCSVFiles = () => {
    return props.dataModel.files || ([] as DataModelFile[]);
  };

  const handleDeleteCsvFile = (csvName: string) => {
    dispatch(deleteCSVFile(csvName));
  };

  const handleDownloadCsvFile = (csvName: string) => {
    dispatch(downloadCSVFile(csvName));
  };

  const getPanelTitle = () => {
    if (props.selectedServiceId) {
      const service = props.services.find(s => s.id === props.selectedServiceId);
      if (service) {
        return service.name;
      }
    }
    return '';
  };

  interface IComponentEvent {
    event: string;
    value: string;
    // eslint-disable-next-line @typescript-eslint/ban-types
    parameters: { [rest: string]: string } | {};
  }

  const sendEvent = (event: IComponentEvent) => {
    MixpanelService.track({
      event: event.event,
      metadata: {
        type: 'Virtual Services',
        value: event.value,
        parameters: event.parameters,
      },
    });
  };

  const sharedFoldersData: SharedFoldersData = {
    bzmSharedFoldersLink: `${props.blazeBaseUrl}/#/settings/accounts/${props.accountId}/workspaces/${props.workspaceId}/shared-folders`,
    isPrivateDataAccount: props.isPrivateDataAccount,
    skip: props.sharedFolders.skip,
    totalSharedFolders: props.sharedFolders.totalSharedFolders,
    workspaceSharedFolders: props.sharedFolders.workspaceSharedFolders,
  };

  function waitUntil(conditionFunction: any) {
    const poll = (resolve: any) => {
      if (conditionFunction()) {
        resolve();
      } else {
        setTimeout(() => poll(resolve), 100);
      }
    };
    return new Promise(poll);
  }

  const onShowMessage = (type: TDMMessageType, message: string, delay?: number) => {
    showTDMMessage(type, message, dispatch, delay);
  };

  const sharedFoldersActions: SharedFoldersActions = {
    createWorkspaceSharedFolder(workspaceId: number, sharedFolder: CreateFolderPayload): Promise<void> {
      dispatch(createFolder(workspaceId, sharedFolder));
      return Promise.resolve(undefined);
    },
    deleteSharedFolderFile(sharedFolderId: string, fileName: string): Promise<void> {
      return new Promise(resolve => {
        dispatch(deleteWorkspaceSharedFolderFile(sharedFolderId, fileName));
        resolve(undefined);
      });
    },
    deleteWorkspaceSharedFolder(workspaceId: number, folderId: string): void {
      dispatch(deleteWorkspaceSharedFolder(workspaceId, folderId));
    },
    fetchWorkspaceSharedFolder(sharedFolderId: string): Promise<SharedFolder> {
      return new Promise(async (resolve, reject) => {
        const res = await fetchFolder(sharedFolderId, store.getState);
        const folder = _.cloneDeep(props.sharedFolders.workspaceSharedFolders.find(sf => sf.id === sharedFolderId));
        if (folder) {
          folder.files = res.files;
          resolve(folder);
        } else {
          reject();
        }
      });
    },
    getTestSharedFolders(): string[] {
      return _.cloneDeep(props.dataModel.sharedFolders) || [];
    },
    loadWorkspaceSharedFolder({ sharedFolderId }: { sharedFolderId: string }): Promise<void> {
      return new Promise(resolve => {
        refLoadingFiles.current = true; // workaround for retrieving a stale flag
        dispatch(loadFolder(sharedFolderId));
        waitUntil(() => !refLoadingFiles.current).then(() => resolve());
      });
    },
    loadWorkspaceSharedFolders(workspaceId: number, paging: Paging): void {
      dispatch(loadFolders(workspaceId, paging));
    },
    showNotification(type: 'success' | 'error' | 'warning' | 'info', message: string, delay: number | undefined): void {
      onShowMessage(type, message, delay);
    },
    updateTestSharedFolders(folders: string[]): void {
      dispatch(setDataModelSharedFolders(folders));

      // we need to wait until all updates of the model are done to get all the current values
      setTimeout(() => {
        dispatch(loadDataModelCsvFiles(folders));
      }, 500);
    },
    uploadFileToSharedFolder({ id, file }: { id: string; file: any }): Promise<void> {
      return new Promise(resolve => {
        dispatch(uploadFileToSharedFolder({ id, file }));
        resolve();
      });
    },
  };

  const getExtensionItems = () => {
    if (_.isNil(props.dataModel.globalEntity)) {
      return;
    }

    return [<GlobalScopeExtension key={'globalScope'} serviceId={props.selectedServiceId} />];
  };

  const getDDPanel = () => {
    return (
      <DataDefinitionPanel
        {...{
          handleUploadCsvFile,
          handleDeleteCsvFile,
          handleDownloadCsvFile,
          onGetCSVFiles,
          panelTitle: getPanelTitle(),
          sendEvent,
          showIterations: !_.isNil(props.showIterations) ? props.showIterations : true, // Show iterations by default
          sharedFoldersData,
          sharedFoldersActions,
          extraPanelItems: getExtensionItems(),
        }}
      />
    );
  };

  const getEmptyPanel = () => (
    <EmptyDataPanel
      isLoading={!!props.dataModel.isLoading}
      onClose={props.onClose}
      isError={props.dataModel.isError}
      onChangeService={props.onChangeService}
      filterServiceIds={props.filterServiceIds}
    />
  );

  return props.selectedServiceId &&
    !props.dataModel.isLoading &&
    !props.dataModel.isError &&
    !!props.dataModel.dataModel
    ? getDDPanel()
    : getEmptyPanel();
};

export default DataDefinitionPanelAdapter;
